import React, { useEffect, useState, useCallback } from 'react';
import { timesheetApi, lookupApi, taskApi, projectApi, activityApi } from '../services/api';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';

export default function TimesheetPage() {
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [timesheet, setTimesheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [trcCodes, setTrcCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // Load lookups once
  useEffect(() => {
    Promise.all([
      lookupApi.trc().then(r => r.data.success ? setTrcCodes(r.data.data || []) : null).catch(() => {}),
      projectApi.activeOnly().then(r => r.data.success ? setProjects(r.data.data || []) : null).catch(() => {}),
      activityApi.list().then(r => r.data.success ? setActivities(r.data.data || []) : null).catch(() => {}),
    ]);
  }, []);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      const res = await timesheetApi.getWeek(dateStr);
      if (res.data.success) {
        const ts = res.data.data;
        setTimesheet(ts);
        if (ts.entries && ts.entries.length > 0) {
          setEntries(ts.entries.map(e => ({
            id: e.id,
            trcCode: e.trcCode || 'REG',
            taskgroup: e.taskgroup || 'QPSUS',
            businessUnit: e.businessUnit || 'TEC00',
            hrDepartment: e.hrDepartment || '3697',
            projectId: e.projectId || '',
            activityId: e.activityId || '',
            hours: e.hours || [0, 0, 0, 0, 0, 0, 0],
          })));
        } else {
          setEntries([createEmptyRow()]);
        }
      } else {
        setTimesheet(null);
        setEntries([createEmptyRow()]);
      }
    } catch (err) {
      setTimesheet(null);
      setEntries([createEmptyRow()]);
    }
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const createEmptyRow = () => ({
    id: null,
    trcCode: 'REG',
    taskgroup: 'QPSUS',
    businessUnit: 'TEC00',
    hrDepartment: '3697',
    projectId: '',
    activityId: '',
    hours: [0, 0, 0, 0, 0, 0, 0],
  });

  const updateHour = (rowIdx, dayIdx, value) => {
    let val = parseFloat(value) || 0;
    if (val < 0) val = 0;
    if (val > 24) val = 24;
    setEntries(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, hours: row.hours.map((h, d) => d === dayIdx ? val : h) } : row
    ));
  };

  const updateField = (rowIdx, field, value) => {
    setEntries(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, [field]: value } : row
    ));
  };

  const addRow = () => setEntries(prev => [...prev, createEmptyRow()]);
  const removeRow = (idx) => {
    if (entries.length > 1) setEntries(prev => prev.filter((_, i) => i !== idx));
  };

  const getRowTotal = (row) => row.hours.reduce((s, h) => s + (h || 0), 0);
  const getDayTotal = (dayIdx) => entries.reduce((s, row) => s + (row.hours[dayIdx] || 0), 0);
  const getGrandTotal = () => entries.reduce((s, row) => s + getRowTotal(row), 0);

  const saveTimesheet = async (action) => {
    // On SUBMIT, require Project and Activity for rows with hours
    if (action === 'SUBMIT') {
      for (let i = 0; i < entries.length; i++) {
        const row = entries[i];
        const rowTotal = getRowTotal(row);
        if (rowTotal > 0) {
          if (!row.projectId) {
            setMessage({ type: 'error', text: `Row ${i + 1}: Please select a Project before submitting.` });
            return;
          }
          if (!row.activityId) {
            setMessage({ type: 'error', text: `Row ${i + 1}: Please select an Activity before submitting.` });
            return;
          }
          if (!row.trcCode) {
            setMessage({ type: 'error', text: `Row ${i + 1}: Please select a TRC code before submitting.` });
            return;
          }
        }
      }
    }

    // Validate daily totals don't exceed 24 hours
    for (let d = 0; d < 7; d++) {
      const dayTotal = getDayTotal(d);
      if (dayTotal > 24) {
        const dayName = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][d];
        setMessage({ type: 'error', text: `${dayName} total is ${dayTotal.toFixed(1)}h — cannot exceed 24 hours per day.` });
        return;
      }
    }
    setSaving(true);
    setMessage(null);
    try {
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      // Default empty TRC to REG
      const cleanEntries = entries.map(e => ({ ...e, trcCode: e.trcCode || 'REG' }));
      const fn = action === 'SUBMIT' ? timesheetApi.submit : timesheetApi.save;
      const res = await fn(dateStr, cleanEntries);
      if (res.data.success) {
        setMessage({ type: 'success', text: action === 'SUBMIT' ? 'Timesheet submitted for approval!' : 'Timesheet saved as draft.' });
        loadWeek();
      } else {
        setMessage({ type: 'error', text: res.data.message || 'Save failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Error saving timesheet' });
    }
    setSaving(false);
  };

  const isEditable = !timesheet?.status ||
    ['DRAFT', 'SAVED', 'SUBMITTED', 'MANAGER_REJECTED', 'HR_REJECTED'].includes(timesheet?.status);

  const getStatusBadge = (status) => {
    const map = {
      DRAFT: 'badge-gray', SAVED: 'badge-info', SUBMITTED: 'badge-warning',
      MANAGER_APPROVED: 'badge-success', HR_APPROVED: 'badge-success',
      MANAGER_REJECTED: 'badge-danger', HR_REJECTED: 'badge-danger',
    };
    return map[status] || 'badge-gray';
  };

  const isToday = (dayIdx) => {
    const d = addDays(weekStart, dayIdx);
    const today = new Date();
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const cellStyle = { width: 52, padding: '4px 2px', fontSize: 13, textAlign: 'center' };
  const selectStyle = { padding: '4px 6px', fontSize: 12, minWidth: 0 };

  return (
    <div>
      <div className="page-header">
        <h1>Timesheet Entry</h1>
        {timesheet?.status && (
          <span className={`badge ${getStatusBadge(timesheet.status)}`} style={{ fontSize: 14, padding: '6px 14px' }}>
            {timesheet.status.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {/* Week Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-sm btn-outline" onClick={() => setWeekStart(prev => subWeeks(prev, 1))}>◀ Prev</button>
          <span style={{ fontWeight: 600, fontSize: 15, padding: '6px 14px', background: '#f0f4f8', borderRadius: 6 }}>
            📅 {format(weekStart, 'MM/dd/yyyy')} – {format(addDays(weekStart, 6), 'MM/dd/yyyy')}
          </span>
          <button className="btn btn-sm btn-outline" onClick={() => setWeekStart(prev => addWeeks(prev, 1))}>Next ▶</button>
          <button className="btn btn-sm btn-outline" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>Today</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Scheduled</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#2563eb' }}>40.0</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Reported</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: getGrandTotal() >= 40 ? '#16a34a' : '#dc2626' }}>{getGrandTotal().toFixed(1)}</div>
          </div>
        </div>
      </div>

      {message && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#16a34a' : '#dc2626'
        }}>
          {message.text}
        </div>
      )}

      {timesheet?.rejectionReason && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, background: '#fee2e2', color: '#dc2626' }}>
          <strong>Rejection Reason:</strong> {timesheet.rejectionReason}
        </div>
      )}

      {/* Time Entry Grid */}
      <div className="card">
        <div className="card-header" style={{ padding: '10px 16px' }}>
          <h3 style={{ fontSize: 14 }}>📝 Time Entry Grid <span className="badge badge-info">{entries.length} rows</span></h3>
          {isEditable && (
            <button className="btn btn-sm btn-outline" onClick={addRow}>+ Add Row</button>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', minWidth: 160, borderBottom: '2px solid #e2e8f0' }}>PROJECT</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', minWidth: 150, borderBottom: '2px solid #e2e8f0' }}>ACTIVITY</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', minWidth: 80, borderBottom: '2px solid #e2e8f0' }}>TRC</th>
                {days.map((d, i) => (
                  <th key={i} style={{
                    ...cellStyle, borderBottom: '2px solid #e2e8f0', fontWeight: 700,
                    background: isToday(i) ? '#dbeafe' : (i >= 5 ? '#f1f5f9' : '#f8fafc'),
                    color: i >= 5 ? '#94a3b8' : '#334155',
                  }}>
                    {dayNames[i]}<br/><span style={{ fontSize: 11, fontWeight: 400 }}>{format(d, 'M/d')}</span>
                  </th>
                ))}
                <th style={{ ...cellStyle, borderBottom: '2px solid #e2e8f0', fontWeight: 700, background: '#f0fdf4', color: '#16a34a' }}>TOTAL</th>
                {isEditable && <th style={{ width: 30, borderBottom: '2px solid #e2e8f0' }}></th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((row, rowIdx) => (
                <tr key={rowIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {/* Project */}
                  <td style={{ padding: '4px 6px' }}>
                    {isEditable ? (
                      <select className="form-control" style={selectStyle} value={row.projectId}
                        onChange={e => updateField(rowIdx, 'projectId', e.target.value)}>
                        <option value="">— Select —</option>
                        {projects.map(p => (
                          <option key={p.id} value={p.projectCode}>{p.projectCode} – {p.projectName}</option>
                        ))}
                      </select>
                    ) : <span style={{ fontSize: 12 }}>{row.projectId || '—'}</span>}
                  </td>
                  {/* Activity */}
                  <td style={{ padding: '4px 6px' }}>
                    {isEditable ? (
                      <select className="form-control" style={selectStyle} value={row.activityId}
                        onChange={e => updateField(rowIdx, 'activityId', e.target.value)}>
                        <option value="">— —</option>
                        {activities.map(a => (
                          <option key={a.id} value={a.code}>{a.code} – {a.description}</option>
                        ))}
                      </select>
                    ) : <span style={{ fontSize: 12 }}>{row.activityId || '—'}</span>}
                  </td>
                  {/* TRC */}
                  <td style={{ padding: '4px 6px' }}>
                    {isEditable ? (
                      <select className="form-control" style={selectStyle} value={row.trcCode}
                        onChange={e => updateField(rowIdx, 'trcCode', e.target.value)}>
                        {trcCodes.map(c => (
                          <option key={c.code} value={c.code}>{c.code}</option>
                        ))}
                      </select>
                    ) : <span style={{ fontSize: 12 }}>{row.trcCode}</span>}
                  </td>
                  {/* Daily Hours */}
                  {row.hours.map((h, dayIdx) => (
                    <td key={dayIdx} style={{
                      ...cellStyle,
                      background: isToday(dayIdx) ? '#dbeafe' : (dayIdx >= 5 ? '#f9fafb' : 'transparent'),
                    }}>
                      {isEditable ? (
                        <input type="number" min="0" max="24" step="0.25"
                          value={h || ''}
                          onChange={e => updateHour(rowIdx, dayIdx, e.target.value)}
                          placeholder="0"
                          style={{ width: 48, textAlign: 'center', padding: '4px 2px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 4 }}
                        />
                      ) : <span>{h || '—'}</span>}
                    </td>
                  ))}
                  {/* Row Total */}
                  <td style={{ ...cellStyle, fontWeight: 700, background: '#f0fdf4', color: '#16a34a' }}>
                    {getRowTotal(row).toFixed(1)}
                  </td>
                  {isEditable && (
                    <td style={{ padding: '4px' }}>
                      <button className="btn btn-sm btn-danger" onClick={() => removeRow(rowIdx)}
                        disabled={entries.length <= 1} style={{ padding: '2px 8px', fontSize: 14 }}>−</button>
                    </td>
                  )}
                </tr>
              ))}

              {/* Daily Totals Row */}
              <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700, fontSize: 12, color: '#64748b' }}>DAILY TOTALS</td>
                {days.map((_, i) => (
                  <td key={i} style={{
                    ...cellStyle, fontWeight: 700, fontSize: 13,
                    color: getDayTotal(i) > 0 ? '#334155' : '#cbd5e1',
                    background: isToday(i) ? '#dbeafe' : (i >= 5 ? '#f1f5f9' : '#f8fafc'),
                  }}>
                    {getDayTotal(i).toFixed(1)}
                  </td>
                ))}
                <td style={{
                  ...cellStyle, fontWeight: 800, fontSize: 16,
                  background: '#dcfce7', color: '#16a34a',
                }}>
                  {getGrandTotal().toFixed(1)}
                </td>
                {isEditable && <td></td>}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Actions */}
        {isEditable && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline" onClick={() => saveTimesheet('SAVE')} disabled={saving}>
                💾 {saving ? 'Saving...' : 'Save for Later'}
              </button>
              <button className="btn btn-primary" onClick={() => saveTimesheet('SUBMIT')} disabled={saving}>
                ✅ {saving ? 'Submitting...' : 'Submit Timesheet'}
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              {entries.filter(r => r.projectId).length} of {entries.length} rows linked to projects
            </div>
          </div>
        )}
      </div>

      {/* Manager Comments */}
      {timesheet?.managerComments && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><h3>Manager Comments</h3></div>
          <div className="card-body"><p>{timesheet.managerComments}</p></div>
        </div>
      )}
    </div>
  );
}
